/**
 * User Management — list users, view details, manage roles & folder access.
 * Matches Actor/Control catalog styling patterns.
 */
import { userService, folderService } from '../services/api';
import { sanitize } from '../services/utils';

let _container: HTMLElement;
let _users: any[] = [];
let _filter = '';

const chipBase = 'display:inline-flex;align-items:center;padding:5px 14px;border-radius:9999px;font-size:13px;cursor:pointer;border:1px solid var(--colorBorder);transition:all .15s;user-select:none';
const chipInactive = 'background:var(--colorBgCard);color:var(--colorTextMuted)';
const labelStyle = 'font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:var(--colorTextLight);margin-bottom:6px';

export async function renderUserMgmt(container: HTMLElement): Promise<void> {
  _container = container;
  const { users } = await userService.list();
  _users = users;
  render();
}

function render(): void {
  const q = _filter.toLowerCase();
  const filtered = q ? _users.filter((u) =>
    (u.display_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
  ) : _users;

  const toolbar = document.getElementById('admin-sub-toolbar');
  if (toolbar) {
    toolbar.innerHTML = `
      <input id="user-filter" type="text" placeholder="Filter..." value="${sanitize(_filter)}"
        style="padding:4px 12px;border:1px solid var(--colorBorderInput);border-radius:9999px;font-size:11px;background:var(--colorBgInput);color:var(--colorText);width:160px;box-sizing:border-box">
      <span style="font-size:11px;color:var(--colorTextMuted)">${_users.length} user${_users.length !== 1 ? 's' : ''}</span>
    `;
  }

  _container.innerHTML = `
    <div style="display:grid;gap:4px">
      ${filtered.length === 0
        ? `<div style="text-align:center;padding:32px 0;color:var(--colorTextLight);font-size:13px">${q ? 'No users match your filter.' : 'No users found.'}</div>`
        : filtered.map((u) => renderUserRow(u)).join('')}
    </div>
  `;

  document.getElementById('user-filter')?.addEventListener('input', (e) => {
    _filter = (e.target as HTMLInputElement).value;
    render();
    const input = document.getElementById('user-filter') as HTMLInputElement;
    if (input) { input.focus(); input.selectionStart = input.selectionEnd = input.value.length; }
  });

  document.querySelectorAll('[data-edit-user]').forEach((el) => {
    el.addEventListener('click', () => {
      const userId = (el as HTMLElement).dataset.editUser!;
      const user = _users.find((u) => u._id === userId);
      if (user) showUserModal(user);
    });
  });
}

function renderUserRow(u: any): string {
  const isAdmin = u.platform_role === 'admin';
  const folders = u.folders || [];
  const folderNames = folders.slice(0, 3).map((f: any) => f.name).join(', ');
  const more = folders.length > 3 ? ` +${folders.length - 3}` : '';

  return `
    <div data-edit-user="${u._id}" style="display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:6px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);cursor:pointer;transition:border-color .15s" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
      <div style="width:32px;height:32px;border-radius:50%;background:var(--colorAccent);display:flex;align-items:center;justify-content:center;color:#fff;font-size:12px;font-weight:600;flex-shrink:0">
        ${sanitize((u.display_name || u.email || '?').charAt(0).toUpperCase())}
      </div>
      <div style="flex:1;min-width:0">
        <div style="font-size:12px;font-weight:500;color:var(--colorTextHeading)">${sanitize(u.display_name || 'No name')}</div>
        <div style="font-size:10px;color:var(--colorTextMuted)">${sanitize(u.email)}</div>
      </div>
      <div style="flex:1;min-width:0;font-size:10px;color:var(--colorTextLight)">
        ${folders.length > 0
          ? `${folders.length} folder${folders.length !== 1 ? 's' : ''} <span style="opacity:.5">· ${sanitize(folderNames)}${more}</span>`
          : '<span style="opacity:.4;font-style:italic">No folders</span>'}
      </div>
      <div style="display:flex;align-items:center;gap:6px;flex-shrink:0">
        <span style="font-size:9px;padding:2px 6px;border-radius:4px;text-transform:uppercase;font-weight:600;${isAdmin ? 'background:var(--colorAccent);color:#fff' : 'background:var(--colorBgCard);border:1px solid var(--colorBorder);color:var(--colorTextMuted)'}">${u.platform_role}</span>
        <span style="font-size:9px;color:var(--colorTextLight)">${u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</span>
      </div>
    </div>
  `;
}

// ─── User Modal ────────────────────────────────────────

function showUserModal(u: any): void {
  const isAdmin = u.platform_role === 'admin';
  const folders = u.folders || [];
  const roleColors: Record<string, string> = { owner: 'var(--colorSuccess)', editor: 'var(--colorAccent)', viewer: 'var(--colorTextMuted)' };

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.style.maxWidth = '600px';
  modal.innerHTML = `
    <!-- User profile header -->
    <div style="display:flex;align-items:flex-start;gap:14px;padding:16px;border-radius:8px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);margin-bottom:20px">
      <div style="width:52px;height:52px;border-radius:50%;background:var(--colorAccent);display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;font-weight:600;flex-shrink:0">
        ${sanitize((u.display_name || u.email || '?').charAt(0).toUpperCase())}
      </div>
      <div style="flex:1;min-width:0">
        <div style="font-size:18px;font-weight:500;color:var(--colorTextHeading);margin-bottom:2px">${sanitize(u.display_name || 'No name')}</div>
        <div style="font-size:13px;color:var(--colorTextMuted)">${sanitize(u.email)}</div>
        <div style="font-size:10px;color:var(--colorTextLight);font-family:monospace;margin-top:4px">${sanitize(u.okta_uid || '—')}</div>
      </div>
    </div>

    <!-- Profile details -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;margin-bottom:18px;font-size:11px">
      <div>
        <div style="${labelStyle}">Last Login</div>
        <div style="color:var(--colorText)">${u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</div>
      </div>
      <div>
        <div style="${labelStyle}">Created</div>
        <div style="color:var(--colorText)">${u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</div>
      </div>
    </div>

    <!-- Platform Role -->
    <div style="margin-bottom:18px">
      <div style="${labelStyle}">Platform Role</div>
      <div id="um-role-tags" style="display:flex;gap:6px">
        <span data-role-val="user" style="${chipBase};${!isAdmin ? 'background:var(--colorBgCard);color:var(--colorTextHeading);border-color:var(--colorAccent)' : chipInactive}">User</span>
        <span data-role-val="admin" style="${chipBase};${isAdmin ? 'background:var(--colorAccent);color:#fff;border-color:var(--colorAccent)' : chipInactive}">Admin</span>
      </div>
      <input type="hidden" id="um-role" value="${u.platform_role}">
    </div>

    <!-- Folder Access -->
    <div style="margin-bottom:20px">
      <div style="${labelStyle}">Folder Access (${folders.length})</div>
      ${folders.length > 0 ? `
        <div style="display:grid;gap:4px;max-height:200px;overflow-y:auto">
          ${folders.map((f: any) => `
            <div style="display:flex;align-items:center;gap:8px;padding:6px 10px;border-radius:6px;background:var(--colorBgCard);border:1px solid var(--colorBorder)">
              <svg width="14" height="14" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
              <a href="#admin/folders/${f._id}" style="flex:1;font-size:12px;color:var(--colorAccent);text-decoration:none" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${sanitize(f.name)}</a>
              <select data-folder-role="${f._id}" data-user-id="${u._id}" style="font-size:10px;padding:2px 6px;border-radius:4px;border:1px solid var(--colorBorder);background:var(--colorBgInput);color:${roleColors[f.role] || 'var(--colorTextMuted)'}">
                <option value="owner" ${f.role === 'owner' ? 'selected' : ''}>Owner</option>
                <option value="editor" ${f.role === 'editor' ? 'selected' : ''}>Editor</option>
                <option value="viewer" ${f.role === 'viewer' ? 'selected' : ''}>Viewer</option>
              </select>
              <button data-remove-from-folder="${f._id}" data-remove-user="${u._id}" style="color:var(--colorError);background:none;border:none;cursor:pointer;font-size:12px;opacity:.5;padding:0 2px" title="Remove from folder">×</button>
            </div>
          `).join('')}
        </div>
      ` : `
        <div style="text-align:center;padding:12px 0;font-size:11px;color:var(--colorTextLight);font-style:italic">
          Not added to any folders yet.
        </div>
      `}
    </div>

    <!-- Actions -->
    <div style="display:flex;gap:8px">
      <button id="um-close" style="flex:1;padding:12px;border-radius:8px;font-size:14px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Close</button>
      <button id="um-save" style="flex:1;padding:12px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Save</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Platform role toggle
  document.querySelectorAll('#um-role-tags span').forEach((el) => {
    el.addEventListener('click', () => {
      const val = (el as HTMLElement).dataset.roleVal!;
      document.querySelectorAll('#um-role-tags span').forEach((s) => {
        (s as HTMLElement).style.cssText = chipBase + ';' + chipInactive;
      });
      if (val === 'admin') {
        (el as HTMLElement).style.cssText = chipBase + ';background:var(--colorAccent);color:#fff;border-color:var(--colorAccent)';
      } else {
        (el as HTMLElement).style.cssText = chipBase + ';background:var(--colorBgCard);color:var(--colorTextHeading);border-color:var(--colorAccent)';
      }
      (document.getElementById('um-role') as HTMLInputElement).value = val;
    });
  });

  // Folder role change
  modal.querySelectorAll('[data-folder-role]').forEach((el) => {
    el.addEventListener('change', async () => {
      const folderId = (el as HTMLSelectElement).dataset.folderRole!;
      const userId = (el as HTMLSelectElement).dataset.userId!;
      const newRole = (el as HTMLSelectElement).value;
      try {
        await folderService.updateMemberRole(folderId, userId, newRole as any);
        (el as HTMLSelectElement).style.color = ({ owner: 'var(--colorSuccess)', editor: 'var(--colorAccent)', viewer: 'var(--colorTextMuted)' } as any)[newRole] || 'var(--colorTextMuted)';
      } catch (err: any) {
        alert(err.message || 'Failed to update role');
      }
    });
  });

  // Remove from folder
  modal.querySelectorAll('[data-remove-from-folder]').forEach((el) => {
    el.addEventListener('click', async () => {
      const folderId = (el as HTMLElement).dataset.removeFromFolder!;
      const userId = (el as HTMLElement).dataset.removeUser!;
      try {
        await folderService.removeMember(folderId, userId);
        const row = (el as HTMLElement).closest('div[style]');
        if (row) row.remove();
        // Update count
        const user = _users.find((uu) => uu._id === userId);
        if (user) {
          user.folders = (user.folders || []).filter((f: any) => f._id !== folderId);
        }
      } catch (err: any) {
        alert(err.message || 'Failed to remove');
      }
    });
  });

  // Close
  modal.querySelector('#um-close')?.addEventListener('click', () => overlay.remove());

  // Save (platform role)
  modal.querySelector('#um-save')?.addEventListener('click', async () => {
    const newRole = (document.getElementById('um-role') as HTMLInputElement).value;
    if (newRole !== u.platform_role) {
      try {
        await userService.updateRole(u._id, newRole as any);
      } catch (err: any) {
        alert(err.message || 'Failed to update role');
        return;
      }
    }
    overlay.remove();
    const { users } = await userService.list();
    _users = users;
    render();
  });
}
